/**
 * Redux Store Configuration
 * Using Redux Toolkit for state management
 */

import { configureStore } from '@reduxjs/toolkit';
import { TokenManager } from '@/shared/api/tokenManager';
import { authSlice } from './slices/authSlice';
import { uiSlice } from './slices/uiSlice';

export const store = configureStore({
    reducer: {
        auth: authSlice.reducer,
        ui: uiSlice.reducer,
    },
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                // Ignore these action types
                ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
            },
        }),
    devTools: import.meta.env.DEV,
});

// Types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Register a state-snapshot hook the diagnostics module reads when
// the "Report a problem" modal submits. The JWT lives in TokenManager,
// not in the store; we only report whether one exists so it never
// lands in a report row.
declare global {
    interface Window {
        __VERO_DIAG_STATE_HOOK__?: () => Record<string, unknown>;
    }
}
if (typeof window !== 'undefined') {
    window.__VERO_DIAG_STATE_HOOK__ = () => {
        const s = store.getState();
        return {
            // Auth — keep identity, redact the token.
            auth: {
                isAuthenticated: s.auth?.isAuthenticated,
                user: s.auth?.user
                    ? { id: s.auth.user.id, email: s.auth.user.email, role: s.auth.user.role }
                    : null,
                token: TokenManager.getToken() ? '<redacted>' : null,
            },
            ui: s.ui,
            // Route + viewport for completeness.
            url: window.location.href,
            viewport: { w: window.innerWidth, h: window.innerHeight },
        };
    };
}

// Export store as default
export default store;
